<template>
  <div>
    <div v-for="(section, index) in sections" :id="section.id" :key="index" class="mb-16 scroll-mt-128">
      <poppins-card class="w-full mx-auto px-16 py-24 md-1:px-24">
        <div class="flex justify-between items-center mb-16">
          <h2 class="text-h4">{{ section.title }}</h2>
          <poppins-button
            v-if="section.editLink && editModeActive"
            value="Edit"
            variant="text"
            v-bind="shouldUseSpaRoutes ? { to: section.editLink } : { href: section.editLink }"
          />
        </div>
        <component :is="section.component" v-if="section.component" v-bind="section.props" v-on="section.listeners || {}" />
      </poppins-card>
    </div>
  </div>
</template>

<script lang="ts">
import pluralize from 'pluralize';
import { camelCase } from 'lodash';
import { FormattedCareType } from '../__types__/CareTypes';
import {
  hasExperiences,
  getExperiences,
  hasSpecialNeedsExperiences,
  getSpecialNeedsExperiences,
} from '../__types__/ExperienceTypes';
import { getCareTypeServices, hasServices } from '../__types__/ServiceTypes';
import { hasHomeworkHelp, getHomeworkHelpData } from '../__types__/HomeworkHelpTypes';
import { hasSkills, getSkills } from '../__types__/SkillsTypes';
import { usesSpaRoutes, getEditUrl } from '../helpers/profileEditUrlHelper';
import PoppinsCard from '@/components/card/Card.vue';
import PoppinsButton from '@/components/button/Button.vue';
import ProfileBio from './profile_sections/ProfileBio.vue';
import ProfileExperience from './profile_sections/ProfileExperience.vue';
import ProfileReviews from './profile_sections/ProfileReviews.vue';
import ProfileTrustAndSafety from './profile_sections/ProfileTrustAndSafety.vue';
import ProfileCertifications from './profile_sections/ProfileCertifications.vue';
import ProfileSchedule from './profile_sections/ProfileSchedule.vue';
import ProfileRates from './profile_sections/ProfileRates.vue';
import ProfileServices from './profile_sections/ProfileServices.vue';
import ProfileHomeworkHelp from './profile_sections/ProfileHomeworkHelp.vue';
import ProfileSkills from './profile_sections/ProfileSkills.vue';
import ProfileSpecialNeedsExperience from './profile_sections/ProfileSpecialNeedsExperience.vue';
import ProfileEducation from './profile_sections/ProfileEducation.vue';
import AdventuresList from '@/components/adventures_list/AdventuresList.vue';
import ProfileCareServicesOffered from './profile_sections/ProfileCareServicesOffered.vue';
import ProfileEmptySectionTemplate from './profile_sections/ProfileEmptySectionTemplate.vue';

interface ProfileSection {
  id?: string;
  title?: string;
  component?: any;
  props?: Record<string, any>;
  listeners?: Record<string, Function>;
  editLink?: string; // Optional link for edit button
}

export default {
  name: 'ProviderProfileSections',
  components: {
    PoppinsCard,
    PoppinsButton,
  },
  props: {
    profileAccountData: {
      default: () => ({}),
      type: Object,
    },
    viewerAccountData: {
      default: () => ({}),
      type: Object,
    },
    reviewsData: {
      default: () => ({}),
      type: Object,
    },
    paramData: {
      type: Object,
      default: () => ({}),
    },
    isProfileOwner: {
      type: Boolean,
      default: false,
    },
    accountTypeInfo: {
      default: () => ({}),
      type: Object,
    },
    editModeActive: {
      type: Boolean,
      default: false,
    },
    previewModeActive: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['view-more-reviews', 'show-edit-mode-popup'],
  data() {
    return {
      hasCalendarAvailability: true,
    };
  },
  computed: {
    formattedCareType(): FormattedCareType {
      return FormattedCareType[this.paramData.careType.toUpperCase()];
    },
    genericCareTypeForUrls(): string {
      if (this.paramData.careType === 'babysitter' || this.paramData.careType === 'nanny') {
        return 'child_care';
      }
      return this.paramData.careType;
    },
    visibleCareServices(): string[] {
      const { careType } = this.paramData;
      const { profiles } = this.profileAccountData;

      if (!profiles) return [];

      const CARE_TYPE_MAP = {
        babysitter: 'BABYSITTER',
        nanny: 'NANNY',
        petCare: 'PET_CARE',
        housekeeping: 'HOUSEKEEPING',
        companionCare: 'COMPANION_CARE',
      };

      const availableCareTypes: string[] = [];

      Object.entries(CARE_TYPE_MAP).forEach(([backendKey, componentKey]) => {
        if (backendKey === camelCase(careType)) {
          return;
        }
        const profile = profiles[backendKey];

        // Profiles are always visible to the owner, but check for completion and visibility for non-owners
        const shouldIncludeProfile = this.editModeActive ? true : profile?.showProfile;

        if (shouldIncludeProfile) {
          availableCareTypes.push(componentKey);
        }
      });

      return availableCareTypes;
    },
    profileData(): any {
      return this.profileAccountData?.profiles?.[this.formattedCareType]?.profile || {};
    },
    sections(): ProfileSection[] {
      const sections = [
        this.getBioSection(),
        this.getExperienceSection(),
        this.getReviewsSection(),
        this.getTrustAndSafetySection(),
        this.getCertificationsSection(),
        this.getScheduleSection(),
        this.getRatesSection(),
        this.getOtherServicesSection(),
        this.getHomeworkHelpSection(),
        this.getSkillsSection(),
        this.getSpecialNeedsExperienceSection(),
        this.getEducationSection(),
        this.getAdventuresSection(),
        this.getCareServicesOfferedSection(),
      ];

      return sections.filter((section) => section !== null) as ProfileSection[];
    },
    baseURL(): string {
      return `/profile/provider/${this.paramData.careType}`;
    },
    shouldUseSpaRoutes(): boolean {
      return usesSpaRoutes(this.paramData.careType);
    },
  },
  methods: {
    scrollToSection(sectionId: string) {
      const element = this.$el.querySelector(`#${sectionId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    getBioSection(): ProfileSection | null {
      const { bio } = this.profileData;
      if (!bio) return null;
      const legacyLink =
        this.paramData.careType === 'housekeeping'
          ? `${this.baseURL}/housekeeping_bio_and_preferences/edit?return_to_profile=true`
          : `${this.baseURL}/${this.paramData.careType}_profile/edit?return_to_profile=true`;

      const editLink = getEditUrl(this.paramData.careType, 'bio', legacyLink);

      return {
        title: `About ${this.profileAccountData?.displayName}`,
        component: ProfileBio,
        props: { bio },
        editLink,
      };
    },
    getExperienceSection(): ProfileSection | null {
      const { yearsExperience } = this.profileData;
      const hasAnyExperiences = hasExperiences(this.profileData, this.formattedCareType);
      const legacyExperienceLink =
        this.paramData.careType === 'housekeeping'
          ? `${this.baseURL}/housekeeping_experience_and_qualifications/edit?return_to_profile=true`
          : `${this.baseURL}/${this.genericCareTypeForUrls}_experience/edit?return_to_profile=true`;
      const editLink = getEditUrl(this.paramData.careType, 'experience', legacyExperienceLink);

      if (!this.editModeActive && !hasAnyExperiences) return null;

      const experienceSectionTitle =
        yearsExperience === 0 ? 'Offers care for' : `${yearsExperience} ${pluralize('year', yearsExperience)} of experience`;

      return {
        title: experienceSectionTitle,
        id: 'experience-section',
        component: ProfileExperience,
        props: {
          careType: this.formattedCareType,
          hasAnyExperiences,
          editModeActive: this.editModeActive,
          yearsExperience,
          ...getExperiences(this.profileData, this.formattedCareType),
          editLink,
          shouldUseSpaRoutes: this.shouldUseSpaRoutes,
        },
        editLink,
      };
    },
    getReviewsSection(): ProfileSection | null {
      if (!this.reviewsData) return null;
      return {
        title: 'Ratings & reviews',
        id: 'reviews-section',
        component: ProfileReviews,
        props: {
          reviewsData: this.reviewsData,
          profileAccountData: this.profileAccountData,
          isProfileOwner: this.isProfileOwner,
          accountTypeInfo: this.accountTypeInfo,
          editModeActive: this.editModeActive,
          previewModeActive: this.previewModeActive,
        },
        listeners: {
          'view-more-reviews': () => this.$emit('view-more-reviews'),
          'show-edit-mode-popup': () => this.$emit('show-edit-mode-popup', 'PREVIEW_REVIEW'),
        },
      };
    },
    getTrustAndSafetySection(): ProfileSection | null {
      const { badges, idv } = this.profileData;

      return {
        title: 'Trust & Safety',
        id: 'trust-safety-section',
        component: ProfileTrustAndSafety,
        props: {
          badges,
          idv,
          viewerAccountData: this.viewerAccountData,
          isProfileOwner: this.isProfileOwner,
          sitterId: this.profileAccountData.id,
          accountTypeInfo: this.accountTypeInfo,
          editModeActive: this.editModeActive,
        },
      };
    },
    getCertificationsSection(): ProfileSection | null {
      // Housekeeping and pet care profiles should not display certification badges
      if (this.formattedCareType === 'housekeeping' || this.formattedCareType === 'petCare') return null;

      const { badges, qualifications } = this.profileData;

      if (!badges) return null;

      // Clone the badges array to avoid mutating the original data
      let modifiedBadges = [...badges];

      let hasCertifications = false;

      if (this.formattedCareType === 'companionCare') {
        // For companion care, update badge statuses based on qualifications array
        const hasFirstAidTraining = qualifications?.includes('FIRST_AID_TRAINING') || false;
        const hasCPRCertified = qualifications?.includes('CPR_CERTIFIED') || false;

        // Map existing badges and update statuses based on qualifications
        modifiedBadges = modifiedBadges.map((badge) => {
          if (badge.name === 'first_aid_training' && hasFirstAidTraining) {
            return { ...badge, status: 'COMPLETE' };
          }
          if (badge.name === 'cpr_certified' && hasCPRCertified) {
            return { ...badge, status: 'COMPLETE' };
          }
          return badge;
        });

        // Add missing badges if they don't exist
        if (hasFirstAidTraining && !modifiedBadges.some((badge) => badge.name === 'first_aid_training')) {
          modifiedBadges.push({ name: 'first_aid_training', status: 'COMPLETE' });
        }
        if (hasCPRCertified && !modifiedBadges.some((badge) => badge.name === 'cpr_certified')) {
          modifiedBadges.push({ name: 'cpr_certified', status: 'COMPLETE' });
        }

        hasCertifications = hasFirstAidTraining || hasCPRCertified;
      } else {
        // For other care types, check badges as before
        const checkBadge = (badgeName) =>
          modifiedBadges?.some((badge) => badge.name === badgeName && badge.status === 'COMPLETE');
        hasCertifications = checkBadge('cpr_certified') || checkBadge('first_aid_training');
      }

      const legacyLink = `${this.baseURL}/${this.genericCareTypeForUrls}_professional_skills/edit?return_to_profile=true`;
      const editLink = getEditUrl(this.paramData.careType, 'certifications', legacyLink);

      if (!hasCertifications) {
        return this.editModeActive
          ? {
              component: ProfileEmptySectionTemplate,
              props: {
                variant: 'certifications',
                btnHref: editLink,
                shouldUseSpaRoutes: this.shouldUseSpaRoutes,
              },
            }
          : null;
      }

      return {
        title: 'Certifications',
        id: 'certifications-section',
        component: ProfileCertifications,
        props: { badges: modifiedBadges, hasCertifications },
        editLink,
      };
    },
    getScheduleSection(): ProfileSection | null {
      const { generalAvailability, availabilityValidUntil } = this.profileData;
      if (this.editModeActive && !generalAvailability?.length && !this.hasCalendarAvailability) {
        return {
          component: ProfileEmptySectionTemplate,
          props: {
            variant: 'schedule',
            btnHref: `${this.baseURL}/${this.paramData.careType}_availability/edit?return_to_profile=true`,
          },
        };
      }

      return {
        title: 'Schedule',
        component: ProfileSchedule,
        props: {
          schedule: generalAvailability,
          sitterName: this.profileAccountData?.displayName,
          availabilityValidUntil,
          sitterId: this.profileAccountData?.id,
          careType: this.paramData.careType,
          editModeActive: this.editModeActive,
        },
        listeners: {
          'update-calendar-availability': (val: boolean) => {
            this.hasCalendarAvailability = val;
          },
        },
      };
    },
    getRatesSection(): ProfileSection | null {
      // Use either 'rates' or 'payRate'
      const { payRate, rates } = this.profileData;
      const effectiveRates = rates;
      const legacyLink =
        this.paramData.careType === 'housekeeping'
          ? `${this.baseURL}/housekeeping_bio_and_preferences/edit?return_to_profile=true`
          : `${this.baseURL}/${this.paramData.careType}_profile/edit?return_to_profile=true`;

      const editLink = getEditUrl(this.paramData.careType, 'rates', legacyLink);

      if (!payRate && !effectiveRates) return null;

      return {
        title: 'Rates',
        id: 'rates-section',
        component: ProfileRates,
        props: {
          careType: this.formattedCareType,
          rates: { payRate, rates: effectiveRates },
          profile: this.profileData,
          editModeActive: this.editModeActive,
          editLink,
        },
        editLink,
      };
    },
    getOtherServicesSection(): ProfileSection | null {
      const services = getCareTypeServices(this.profileData, this.formattedCareType);

      let legacyLink = `${this.baseURL}/${this.genericCareTypeForUrls}_additional_services/edit?return_to_profile=true`;
      if (this.paramData.careType === 'housekeeping') {
        legacyLink = `${this.baseURL}/housekeeping_bio_and_preferences/edit?return_to_profile=true`;
      }
      if (this.paramData.careType === 'pet_care') {
        legacyLink = `${this.baseURL}/pet_care_experience/edit?return_to_profile=true`;
      }

      const editLink = getEditUrl(this.paramData.careType, 'services', legacyLink);
      const isHousekeeping = this.formattedCareType === 'housekeeping';

      if (!hasServices({ [this.formattedCareType]: services }, this.formattedCareType)) {
        return this.editModeActive
          ? {
              component: ProfileEmptySectionTemplate,
              props: {
                variant: isHousekeeping ? 'services' : 'other_services',
                btnHref: editLink,
                shouldUseSpaRoutes: this.shouldUseSpaRoutes,
              },
            }
          : null;
      }

      return {
        title: this.formattedCareType === 'housekeeping' ? 'Services' : 'Other services',
        component: ProfileServices,
        props: { careType: this.formattedCareType, services },
        editLink,
      };
    },
    getHomeworkHelpSection(): ProfileSection | null {
      const legacyLink = `${this.baseURL}/${this.genericCareTypeForUrls}_additional_services/edit?return_to_profile=true`;
      const editLink = getEditUrl(this.paramData.careType, 'homework-help', legacyLink);
      const hasExp = hasHomeworkHelp({ ...this.profileData });

      if (!this.profileData.homeworkHelp || (!hasExp && !this.editModeActive)) return null;
      if (!hasExp && this.editModeActive) {
        return {
          component: ProfileEmptySectionTemplate,
          props: {
            variant: 'homework_help',
            btnHref: editLink,
            shouldUseSpaRoutes: this.shouldUseSpaRoutes,
          },
        };
      }

      const { homeworkHelpType, homeworkHelpSubjects } = getHomeworkHelpData({ ...this.profileData });
      return {
        title: 'Homework help',
        component: ProfileHomeworkHelp,
        props: {
          homeworkHelpType,
          homeworkHelpSubjects,
        },
        editLink,
      };
    },
    getSkillsSection(): ProfileSection | null {
      const editLink = `${this.baseURL}/${this.genericCareTypeForUrls}_professional_skills/edit?return_to_profile=true`;
      if (!hasSkills(this.profileData, this.formattedCareType)) {
        return this.editModeActive
          ? {
              component: ProfileEmptySectionTemplate,
              props: {
                variant: 'specialty_skills',
                btnHref: this.shouldUseSpaRoutes ? getEditUrl(this.paramData.careType, 'specialty-skills', editLink) : editLink,
                shouldUseSpaRoutes: this.shouldUseSpaRoutes,
              },
            }
          : null;
      }
      const skills = getSkills(this.profileData, this.formattedCareType);
      return {
        title: 'Specialty skills',
        component: ProfileSkills,
        props: {
          careType: this.formattedCareType,
          ...skills,
        },
        editLink: this.shouldUseSpaRoutes ? getEditUrl(this.paramData.careType, 'specialty-skills', editLink) : editLink,
      };
    },
    getSpecialNeedsExperienceSection(): ProfileSection | null {
      const legacyLink = `${this.baseURL}/special_needs/edit?return_to_profile=true`;
      const editLink = getEditUrl(this.paramData.careType, 'special-needs', legacyLink);
      const hasExp = hasSpecialNeedsExperiences(this.profileData);

      if (!this.profileData.specialNeeds || (!hasExp && !this.editModeActive)) return null;
      if (!hasExp && this.editModeActive) {
        return {
          component: ProfileEmptySectionTemplate,
          props: {
            variant: 'special_needs_experience',
            btnHref: editLink,
            shouldUseSpaRoutes: this.shouldUseSpaRoutes,
          },
        };
      }

      return {
        title: 'Special needs experience with',
        component: ProfileSpecialNeedsExperience,
        props: {
          ...getSpecialNeedsExperiences(this.profileData),
        },
        editLink,
      };
    },
    getEducationSection(): ProfileSection | null {
      const { education } = this.profileData;
      const editLink = `${this.baseURL}/${this.genericCareTypeForUrls}_professional_skills/edit?return_to_profile=true`;
      if (!education || !education.schoolName) {
        return this.editModeActive
          ? {
              component: ProfileEmptySectionTemplate,
              props: {
                variant: 'education',
                btnHref: this.shouldUseSpaRoutes ? getEditUrl(this.paramData.careType, 'education', editLink) : editLink,
                shouldUseSpaRoutes: this.shouldUseSpaRoutes,
              },
            }
          : null;
      }
      return {
        title: 'Education',
        component: ProfileEducation,
        props: { careType: this.formattedCareType, education },
        editLink: this.shouldUseSpaRoutes ? getEditUrl(this.paramData.careType, 'education', editLink) : editLink,
      };
    },
    getAdventuresSection(): ProfileSection | null {
      const isChildCare = this.formattedCareType === 'babysitter' || this.formattedCareType === 'nanny';
      if (!isChildCare) return null;

      const adventuresEdges = this.profileAccountData?.adventures?.edges || [];
      const allAdventures = adventuresEdges.map(({ node }: any) => node.adventure);
      const activeAdventures = allAdventures.filter((adventure: any) => adventure.status === 'active');

      if (!activeAdventures || activeAdventures.length === 0) return null;

      const displayName = this.profileAccountData?.displayName || '';

      return {
        title: 'Activity-inspired care',
        component: AdventuresList,
        props: {
          adventures: activeAdventures,
          sitter: {
            location: {
              stateabbr: this.profileAccountData?.location?.stateabbr || '',
              cityname: this.profileAccountData?.location?.cityname || '',
            },
          },
          variant: 'profile',
          displayName,
          returnPath: `/provider/profile/${this.paramData.careType}/${this.profileAccountData?.id}`,
        },
      };
    },
    getCareServicesOfferedSection(): ProfileSection | null {
      if (!this.visibleCareServices.length) return null;
      return {
        title: 'Additional care offered',
        component: ProfileCareServicesOffered,
        props: {
          visibleCareServices: this.visibleCareServices,
          sitterUuid: this.paramData.sitterUuid,
          accountTypeInfo: this.accountTypeInfo,
        },
      };
    },
  },
};
</script>
